import React from 'react';
import styled from 'styled-components';
import BackGroundImg from '../images/purple-web.png';

const PrivacyPolicy = () => {
  return (
    <Wrapper>
      <Container>
        <Title>Privacy Policy</Title>
        <LastUpdated>
          Last Updated: {new Date().toLocaleDateString()}
        </LastUpdated>

        <Section>
          <SectionTitle>1. Introduction</SectionTitle>
          <Text>
            This Privacy Policy describes how Abel's Personal Website ("we",
            "our", or "us") collects, uses, and protects your information when
            you use our website at abeliciousness.com.
          </Text>
        </Section>

        <Section>
          <SectionTitle>2. Information We Collect</SectionTitle>
          <Text>
            <strong>Google User Data:</strong> When you sign in with Google, we
            collect the following information:
          </Text>
          <List>
            <ListItem>Email address</ListItem>
            <ListItem>Display name</ListItem>
            <ListItem>Profile photo URL</ListItem>
            <ListItem>User ID (unique identifier)</ListItem>
          </List>
          <Text>
            <strong>User-Generated Content:</strong> We collect comments, media
            uploads, and voice recordings that you voluntarily submit through
            our comment system.
          </Text>
        </Section>

        <Section>
          <SectionTitle>3. How We Use Your Information</SectionTitle>
          <Text>
            We use the collected information for the following purposes:
          </Text>
          <List>
            <ListItem>
              <strong>Authentication:</strong> To authenticate your identity
              when you sign in with Google
            </ListItem>
            <ListItem>
              <strong>User Experience:</strong> To display your name and profile
              photo in comments and interactions
            </ListItem>
            <ListItem>
              <strong>Content Management:</strong> To associate your comments
              and media uploads with your account
            </ListItem>
            <ListItem>
              <strong>Website Functionality:</strong> To provide and improve the
              core functionality of our website
            </ListItem>
          </List>
          <Text>
            <strong>We do NOT use Google user data for:</strong>
          </Text>
          <List>
            <ListItem>Advertising or marketing purposes</ListItem>
            <ListItem>
              Sharing with third parties for commercial purposes
            </ListItem>
            <ListItem>
              Any purpose other than providing or improving our website's
              functionality
            </ListItem>
          </List>
        </Section>

        <Section>
          <SectionTitle>4. Data Sharing and Disclosure</SectionTitle>
          <Text>
            <strong>
              We do NOT sell, rent, or share your Google user data with third
              parties.
            </strong>
          </Text>
          <Text>
            Your data is stored securely using Firebase (Google Cloud Platform)
            services:
          </Text>
          <List>
            <ListItem>
              <strong>Firebase Authentication:</strong> Handles user
              authentication securely
            </ListItem>
            <ListItem>
              <strong>Cloud Firestore:</strong> Stores your comments and user
              data
            </ListItem>
            <ListItem>
              <strong>Firebase Storage:</strong> Stores media files you upload
            </ListItem>
          </List>
          <Text>
            These services are provided by Google and are subject to Google's
            privacy policies. We only use these services to provide our website
            functionality.
          </Text>
        </Section>

        <Section>
          <SectionTitle>5. Data Protection</SectionTitle>
          <Text>
            We implement the following security measures to protect your data:
          </Text>
          <List>
            <ListItem>
              All data transmission is encrypted using HTTPS/TLS
            </ListItem>
            <ListItem>
              User authentication is handled securely through Firebase
              Authentication
            </ListItem>
            <ListItem>
              Access to user data is restricted through Firebase Security Rules
            </ListItem>
            <ListItem>
              Input sanitization to prevent security vulnerabilities
            </ListItem>
            <ListItem>Rate limiting to prevent abuse</ListItem>
          </List>
        </Section>

        <Section>
          <SectionTitle>6. Data Retention and Deletion</SectionTitle>
          <Text>
            <strong>Data Retention:</strong> We retain your Google user data and
            user-generated content for as long as your account is active or as
            needed to provide our services.
          </Text>
          <Text>
            <strong>Data Deletion:</strong> You can request deletion of your
            data at any time by:
          </Text>
          <List>
            <ListItem>
              Contacting us through the contact form on our website
            </ListItem>
            <ListItem>
              Deleting your account through your account settings (if available)
            </ListItem>
          </List>
          <Text>
            Upon receiving a deletion request, we will delete your personal data
            within 30 days, except where we are required to retain it for legal
            purposes.
          </Text>
        </Section>

        <Section>
          <SectionTitle>7. Your Rights</SectionTitle>
          <Text>You have the right to:</Text>
          <List>
            <ListItem>Access your personal data</ListItem>
            <ListItem>Request correction of inaccurate data</ListItem>
            <ListItem>Request deletion of your data</ListItem>
            <ListItem>
              Revoke Google OAuth access at any time through your Google account
              settings
            </ListItem>
          </List>
        </Section>

        <Section>
          <SectionTitle>8. Cookies and Local Storage</SectionTitle>
          <Text>
            We use browser local storage to maintain your session and
            preferences. This includes authentication state and age verification
            status.
          </Text>
        </Section>

        <Section>
          <SectionTitle>9. Third-Party Services</SectionTitle>
          <Text>Our website uses the following third-party services:</Text>
          <List>
            <ListItem>
              <strong>Google Firebase:</strong> For authentication, database,
              and storage services
            </ListItem>
            <ListItem>
              <strong>Google Identity Services:</strong> For Google Sign-In
              functionality
            </ListItem>
            <ListItem>
              <strong>YouTube:</strong> Embedded videos (subject to YouTube's
              privacy policy)
            </ListItem>
            <ListItem>
              <strong>Spotify:</strong> Embedded playlists (subject to Spotify's
              privacy policy)
            </ListItem>
          </List>
        </Section>

        <Section>
          <SectionTitle>10. Children's Privacy</SectionTitle>
          <Text>
            Our website requires age verification. We do not knowingly collect
            personal information from users under the age of 18 without
            appropriate consent.
          </Text>
        </Section>

        <Section>
          <SectionTitle>11. Changes to This Privacy Policy</SectionTitle>
          <Text>
            We may update this Privacy Policy from time to time. We will notify
            users of any material changes by updating the "Last Updated" date at
            the top of this page.
          </Text>
        </Section>

        <Section>
          <SectionTitle>12. Contact Us</SectionTitle>
          <Text>
            If you have questions about this Privacy Policy or wish to exercise
            your rights, please contact us through the contact form on our
            website.
          </Text>
        </Section>

        <BackLink href="#home">← Back to Home</BackLink>
      </Container>
    </Wrapper>
  );
};

export default PrivacyPolicy;

const Wrapper = styled.div`
  min-height: 100vh;
  background: url(${BackGroundImg});
  background-size: cover;
  background-position: center;
  padding: 40px 20px;
  color: white;
  font-family: 'Creepster', Arial, sans-serif;
`;

const Container = styled.div`
  max-width: 900px;
  margin: 0 auto;
  background: rgba(0, 0, 0, 0.8);
  padding: 40px;
  border-radius: 15px;
  border: 2px solid rgba(76, 0, 101, 0.6);
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.5);

  @media (max-width: 768px) {
    padding: 20px;
  }
`;

const Title = styled.h1`
  font-size: 48px;
  text-align: center;
  margin-bottom: 10px;
  color: #4c0065;
  text-shadow: 2px 2px 8px #000000;

  @media (max-width: 768px) {
    font-size: 36px;
  }
`;

const LastUpdated = styled.p`
  text-align: center;
  color: rgba(255, 255, 255, 0.7);
  margin-bottom: 30px;
  font-size: 14px;
`;

const Section = styled.div`
  margin-bottom: 30px;
`;

const SectionTitle = styled.h2`
  font-size: 28px;
  color: #4c0065;
  margin-bottom: 15px;
  text-shadow: 1px 1px 4px #000000;

  @media (max-width: 768px) {
    font-size: 22px;
  }
`;

const Text = styled.p`
  font-size: 16px;
  line-height: 1.6;
  margin-bottom: 15px;
  color: rgba(255, 255, 255, 0.9);

  @media (max-width: 768px) {
    font-size: 14px;
  }
`;

const List = styled.ul`
  margin: 15px 0;
  padding-left: 30px;
`;

const ListItem = styled.li`
  font-size: 16px;
  line-height: 1.6;
  margin-bottom: 10px;
  color: rgba(255, 255, 255, 0.9);

  @media (max-width: 768px) {
    font-size: 14px;
  }
`;

const BackLink = styled.a`
  display: inline-block;
  margin-top: 30px;
  padding: 12px 24px;
  background-color: #4c0065;
  color: white;
  text-decoration: none;
  border-radius: 8px;
  font-size: 18px;
  transition: all 0.3s ease;

  &:hover {
    background-color: #6a0088;
    transform: scale(1.05);
  }
`;
